export type ServiceShowcaseMediaItem = {
  id: string
  type: "PHOTO" | "VIDEO"
  title: string
  description: string
  imageUrl: string
  imageKey: string
  thumbnailUrl: string
  thumbnailKey: string
  videoUrl: string
  videoKey: string
  beforeImageUrl?: string
  beforeImageKey?: string
  badge?: string
  area?: string
}

export type ServiceReviewSource = {
  id: string
  channel: "WEBSITE" | "GOOGLE"
  label: string
  enabled: boolean
  sourceUrl: string
  placeId: string
  notes: string
}

export type ServiceReviewItem = {
  id: string
  channel: "WEBSITE" | "GOOGLE"
  author: string
  role: string
  rating: number
  comment: string
  reviewUrl: string
  publishedAt: string
  status: "PENDING" | "APPROVED" | "REJECTED"
}

export const DEFAULT_SERVICE_SHOWCASE_ITEMS: ServiceShowcaseMediaItem[] = [
  {
    id: "appartement-photo",
    type: "PHOTO",
    title: "Nettoyage d’appartement",
    description: "Photo finale d’un appartement nettoye.",
    imageUrl: "",
    imageKey: "",
    thumbnailUrl: "",
    thumbnailKey: "",
    videoUrl: "",
    videoKey: "",
    beforeImageUrl: "",
    beforeImageKey: "",
    badge: "Nettoyage",
    area: "Particuliers a Sevrier",
  },
  {
    id: "bureaux-video",
    type: "VIDEO",
    title: "Entretien de bureaux",
    description: "Video ou miniature d’une intervention en entreprise.",
    imageUrl: "",
    imageKey: "",
    thumbnailUrl: "",
    thumbnailKey: "",
    videoUrl: "",
    videoKey: "",
    beforeImageUrl: "",
    beforeImageKey: "",
    badge: "Video terrain",
    area: "Bureaux et commerces",
  },
  {
    id: "apres-travaux-photo",
    type: "PHOTO",
    title: "Remise en etat apres travaux",
    description: "Resultat final ou avant/apres d’un chantier.",
    imageUrl: "",
    imageKey: "",
    thumbnailUrl: "",
    thumbnailKey: "",
    videoUrl: "",
    videoKey: "",
    beforeImageUrl: "",
    beforeImageKey: "",
    badge: "Avant / apres",
    area: "Fin de chantier",
  },
  {
    id: "vitrerie-photo",
    type: "PHOTO",
    title: "Vitrerie et finitions",
    description: "Finitions detaillees apres intervention.",
    imageUrl: "",
    imageKey: "",
    thumbnailUrl: "",
    thumbnailKey: "",
    videoUrl: "",
    videoKey: "",
    beforeImageUrl: "",
    beforeImageKey: "",
    badge: "Finitions",
    area: "Tous secteurs",
  },
]

export const DEFAULT_SERVICE_REVIEW_SOURCES: ServiceReviewSource[] = [
  {
    id: "website-reviews",
    channel: "WEBSITE",
    label: "Avis du site",
    enabled: true,
    sourceUrl: "",
    placeId: "",
    notes: "Formulaire ou espace de collecte des avis du site vitrine.",
  },
  {
    id: "google-business-profile",
    channel: "GOOGLE",
    label: "Google Business Profile",
    enabled: true,
    sourceUrl: "",
    placeId: "",
    notes: "Lien de la fiche Google ou identifiant Place ID pour synchro future.",
  },
]

export const DEFAULT_SERVICE_REVIEW_ITEMS: ServiceReviewItem[] = []

function asTrimmedString(value: any) {
  return String(value || "").trim()
}

export function normalizeServiceShowcaseItems(value: any): ServiceShowcaseMediaItem[] {
  const items = Array.isArray(value) ? value : []

  if (!items.length) {
    return DEFAULT_SERVICE_SHOWCASE_ITEMS.map((item) => ({ ...item }))
  }

  return items.map((item: any, index: number) => {
    const fallback = DEFAULT_SERVICE_SHOWCASE_ITEMS[index] || DEFAULT_SERVICE_SHOWCASE_ITEMS[0]
    return {
      id: asTrimmedString(item?.id) || fallback.id,
      type: (item?.type === "VIDEO" ? "VIDEO" : "PHOTO") as "PHOTO" | "VIDEO",
      title: asTrimmedString(item?.title) || fallback.title,
      description: asTrimmedString(item?.description) || fallback.description,
      imageUrl: asTrimmedString(item?.imageUrl),
      imageKey: asTrimmedString(item?.imageKey),
      thumbnailUrl: asTrimmedString(item?.thumbnailUrl),
      thumbnailKey: asTrimmedString(item?.thumbnailKey),
      videoUrl: asTrimmedString(item?.videoUrl),
      videoKey: asTrimmedString(item?.videoKey),
      beforeImageUrl: asTrimmedString(item?.beforeImageUrl),
      beforeImageKey: asTrimmedString(item?.beforeImageKey),
      badge: asTrimmedString(item?.badge),
      area: asTrimmedString(item?.area),
    }
  })
}

export function normalizeServiceReviewSources(value: any): ServiceReviewSource[] {
  const items = Array.isArray(value) ? value : []
  const normalized: ServiceReviewSource[] = items
    .map((item: any, index: number) => {
      const fallback = DEFAULT_SERVICE_REVIEW_SOURCES[index] || DEFAULT_SERVICE_REVIEW_SOURCES[0]
      return {
        id: asTrimmedString(item?.id) || fallback.id,
        channel: (item?.channel === "GOOGLE" ? "GOOGLE" : "WEBSITE") as "WEBSITE" | "GOOGLE",
        label: asTrimmedString(item?.label) || fallback.label,
        enabled: item?.enabled !== false,
        sourceUrl: asTrimmedString(item?.sourceUrl),
        placeId: asTrimmedString(item?.placeId),
        notes: asTrimmedString(item?.notes) || fallback.notes,
      }
    })
    .slice(0, DEFAULT_SERVICE_REVIEW_SOURCES.length)

  if (normalized.length === DEFAULT_SERVICE_REVIEW_SOURCES.length) {
    return normalized
  }

  return DEFAULT_SERVICE_REVIEW_SOURCES.map((fallback, index) => normalized[index] || fallback)
}

export function normalizeServiceReviewItems(value: any): ServiceReviewItem[] {
  const items = Array.isArray(value) ? value : []

  return items.map((item: any, index: number) => ({
    id: asTrimmedString(item?.id) || `review_${index + 1}`,
    channel: (item?.channel === "GOOGLE" ? "GOOGLE" : "WEBSITE") as "WEBSITE" | "GOOGLE",
    author: asTrimmedString(item?.author),
    role: asTrimmedString(item?.role),
    rating: Math.max(1, Math.min(5, Number(item?.rating) || 5)),
    comment: asTrimmedString(item?.comment),
    reviewUrl: asTrimmedString(item?.reviewUrl),
    publishedAt: asTrimmedString(item?.publishedAt),
    status:
      item?.status === "APPROVED"
        ? "APPROVED"
        : item?.status === "REJECTED"
          ? "REJECTED"
          : "PENDING",
  }))
}

export function splitServiceHighlights(value: string) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
}
